import { atom } from "jotai";
import { Node, Edge } from "@xyflow/react";
import { addError } from "@zapier/observability";
import { Message } from "@ai-sdk/ui-utils";

import { saveCanvasSetter } from "../persistence";
import { canvasAtom, canvasIdAtom, streamingProgressAtom } from "../canvas";
import { draftFocusNodeIdAtom, nodesAtom } from "../nodes";
import { edgesAtom } from "../edges";
import { isViewOnlyModeAtom } from "../permissions";
import {
  assignAssetsAndAttributionsToCanvas,
  convertAssetIdsToPlaceholderIds,
  removeTemplateFromString,
  attributeBackendNodesByType,
  isValidUUID,
  processZaps,
} from "../utils";
import { ZSLFromTemplate } from "../types";
import { zslAtom } from "./zslAtom";

import { aiPromptAtom } from "../../../streaming/src/atoms/aiPromptAtom";
import { programmaticNavigationExpectedAtom } from "../navigation";
import { emitZslExecutionErrorEventSetter } from "../events";
import { isDraftModeExcludingTemplatesAtom } from "../nodes";
import { isExecutingZslAtom } from "./isExecutingZslAtom";
import { isTemplatePageAtom } from "./isTemplatePageAtom";
import { buildInformedAssistantMessage } from "../copilot";

type CanvasToSave = {
  nodes: Node[];
  edges: Edge[];
  name?: string;
  description?: string;
};

// Define type for copilot chat service based on what we use
// Using any for now since CopilotContextValue is not exported from ai-toolkit/client
type CopilotChatService = any;

type Params = {
  prompt?: string;
  copilot: any;
  conversationId: string;
  onError?: (errorMessage: string) => void;
};

// Helper function to add message to thread and submit it
const addMessageToThread = (
  service: NonNullable<CopilotChatService>,
  isError: boolean,
  errorMessage?: string,
  zslData?: any,
) => {
  if (!service) {
    return;
  }

  // Handle AI toolkit copilot service
  const copilotService = service as CopilotChatService;
  const nextMessages = buildInformedAssistantMessage({
    isError,
    errorMessage,
    toolArgs: { zsl: zslData || {} },
    successMessage: "Successfully executed ZSL template",
  });
  copilotService.setMessages((prevMessages: Message[]) => [
    ...prevMessages,
    ...nextMessages,
  ]);
  if (isError) {
    copilotService.append({
      role: "user",
      content: "Fix the error in my canvas.",
    });
  }
};

// Atom setter to handle ZSL execution errors consistently
const handleZslExecutionErrorSetter = atom(
  null,
  (
    get,
    set,
    {
      error,
      statusCode,
      copilot,
      zslData,
      onError,
    }: {
      error: any;
      statusCode: number;
      copilot: any;
      zslData: any;
      onError?: (errorMessage: string) => void;
    },
  ) => {
    // Get all state from atoms
    const isTemplatePage = get(isTemplatePageAtom);
    const isDraftModeNotTemplates = get(isDraftModeExcludingTemplatesAtom);
    const prompt = get(aiPromptAtom) as string;

    const errorMessage = error.message || JSON.stringify(error);

    addMessageToThread(copilot, true, errorMessage, zslData);
    if (onError) {
      onError(errorMessage);
    }

    const isAiPage = isDraftModeNotTemplates;

    set(emitZslExecutionErrorEventSetter, {
      prompt,
      zsl: JSON.stringify(zslData),
      status_code: statusCode,
      error_message: errorMessage,
      is_ai_page: isAiPage,
      is_template_page: isTemplatePage,
    });

    addError(error, {
      source: "handleZslExecutionErrorSetter",
      context: {
        name: "zsl_execution_error",
        zsl: zslData,
        statusCode,
      },
    });
  },
);

handleZslExecutionErrorSetter.debugLabel = "handleZslExecutionErrorSetter";

function trimString(inputString: string) {
  const maxLength = 99; // Canvas description must NOT have more than 100 characters
  const ellipsis = "...";

  if (inputString.length > maxLength) {
    return inputString.substring(0, maxLength - ellipsis.length) + ellipsis;
  }

  return inputString;
}

/* Converts template to real canvas nodes and edges
 * Used by AiCopilotBanner, template page, and AI page
 */
export const executeZslAndSaveSetter = atom(
  null,
  async (get, set, params: Params) => {
    const { prompt = "", copilot, conversationId, onError } = params;

    // Get the current canvas state
    const zsl = get(zslAtom);
    if (zsl === undefined) {
      return;
    }

    const canvasId = get(canvasIdAtom);
    set(isExecutingZslAtom, true);
    let processedZsl = { ...zsl };

    // For template or AI pages with no real canvas
    // nodes and edges will need to be backend versions since zsl-api uses v2 API to create a canvas
    if (!canvasId || !isValidUUID(canvasId)) {
      if (zsl) {
        processedZsl = processZaps(processedZsl);

        if (zsl.canvases && zsl.canvases.length > 0) {
          // For template page, there will be canvases
          const processedCanvases = zsl.canvases.map((canvas) => {
            return {
              ...canvas,
              nodes: attributeBackendNodesByType(canvas.nodes, "TEMPLATE"),
            };
          });

          processedZsl = {
            ...processedZsl,
            canvases: processedCanvases,
          };
        } else {
          // For AI page, there will be NO canvases
          const nodes = get(nodesAtom);
          const edges = get(edgesAtom);

          const { nodes: backendNodes, edges: backendEdges } =
            convertAssetIdsToPlaceholderIds({ nodes, edges });

          processedZsl = {
            ...processedZsl,
            canvases: [
              {
                id: "_CANVAS_1",
                name: zsl.name,
                description: trimString(zsl.description || ""),
                nodes: backendNodes,
                edges: backendEdges,
              },
            ],
          };
        }

        try {
          const zslRes = await fetch("/api/canvasui/zsl/execute", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              template: {
                meta: {},
                ...processedZsl,
              },
            }),
          });
          if (!zslRes.ok) {
            const error = await zslRes.json();
            const statusCode = zslRes.status;
            set(handleZslExecutionErrorSetter, {
              error,
              statusCode,
              copilot,
              zslData: processedZsl,
              onError,
            });

            return;
          }

          const zsl = await zslRes.json();
          set(isViewOnlyModeAtom, false);

          const canvasIsNotEmpty = Object.keys(zsl.data.canvases).length > 0;

          if (canvasIsNotEmpty) {
            // Set the flag before navigating
            set(programmaticNavigationExpectedAtom, true);
            // Build URL with appropriate query parameters
            const canvasId = Object.keys(zsl.data.canvases)[0];
            const params = new URLSearchParams();

            // Copilot provides tour suggestion buttons, so we don't auto-start tours

            if (conversationId) {
              params.set("conversationId", conversationId);
            }

            // Defer navigation with a slightly longer delay
            setTimeout(() => {
              window.location.href = `/app/canvas/${canvasId}?${params.toString()}`;
            }, 100); // Increased delay to 100ms
          }
        } catch (error) {
          const statusCode = error instanceof Response ? error.status : 500;
          set(handleZslExecutionErrorSetter, {
            error,
            statusCode,
            copilot,
            zslData: processedZsl,
            onError,
          });

          // Reset the flag on error
          set(programmaticNavigationExpectedAtom, false);
        } finally {
          set(isExecutingZslAtom, false);
        }
      }
    } else {
      // Exclude canvases since AiCopilotBanner already has canvas
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { canvases, ...zslNoCanvas } = zsl!;

      // Process zaps to truncate titles if they exist
      const processedZslNoCanvas = processZaps(zslNoCanvas);

      try {
        const zslRes = await fetch("/api/canvasui/zsl/execute", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            template: {
              meta: {},
              ...processedZslNoCanvas,
            },
          }),
        });
        if (!zslRes.ok) {
          const error = await zslRes.json();
          const statusCode = zslRes.status;
          set(handleZslExecutionErrorSetter, {
            error,
            statusCode,
            copilot,
            zslData: processedZslNoCanvas,
            onError,
          });

          return;
        }

        const result: ZSLFromTemplate = await zslRes.json();

        const canvas = get(canvasAtom);
        const nodes = get(nodesAtom);
        const edges = get(edgesAtom);

        const hydratedCanvas = assignAssetsAndAttributionsToCanvas(
          { ...canvas, nodes, edges }, // Pass in current nodes & edges
          result,
          prompt,
        );

        /* Clearing out template now that it's been used so saving nodes and edges will persist.

        This needs to go before setting the nodes so that zapAtomFamily_SUSPENDS will make a fetch call with the real zap ids or else it will try to find the zap in the template */
        set(zslAtom, undefined);
        set(nodesAtom, hydratedCanvas.nodes);
        set(edgesAtom, hydratedCanvas.edges);

        const canvasToSave: CanvasToSave = {
          nodes: hydratedCanvas.nodes,
          edges: hydratedCanvas.edges,
        };

        const newCanvasName = canvas.name
          ? undefined
          : removeTemplateFromString(processedZslNoCanvas.name);

        if (newCanvasName) {
          // Only set the new zsl name as canvas name when user hasn't named their canvas
          set(canvasAtom, (prevCanvas) => ({
            ...prevCanvas,
            name: newCanvasName,
          }));
          canvasToSave.name = newCanvasName;
        }

        const newCanvasDescription = canvas.description
          ? undefined
          : processedZslNoCanvas.description;

        if (newCanvasDescription) {
          // Only set the new zsl description as canvas description when user hasn't added a description
          set(canvasAtom, (prevCanvas) => ({
            ...prevCanvas,
            description: newCanvasDescription,
          }));
          canvasToSave.description = newCanvasDescription;
        }

        set(isViewOnlyModeAtom, false);
        set(saveCanvasSetter, canvasToSave);
        set(streamingProgressAtom, 0);
        set(draftFocusNodeIdAtom, null); // prevents DraftAssetPreview from crashing after build

        // Send success message
        addMessageToThread(copilot, false, undefined, processedZslNoCanvas);
      } catch (error) {
        const statusCode = error instanceof Response ? error.status : 500;
        set(handleZslExecutionErrorSetter, {
          error,
          statusCode,
          copilot,
          zslData: processedZslNoCanvas,
          onError,
        });
      } finally {
        set(isExecutingZslAtom, false);
      }
    }
  },
);

executeZslAndSaveSetter.debugLabel = "executeZslAndSaveSetter";
